import { Draft, PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  GeofenceStateParam,
  HealthStatus,
  ObjectType,
  SoftwareStatus,
} from '@casops/store/apis/commonModels';
import { AssetDeviceType } from '@casops/store/apis/registeredAssetsApi';
import { authApi } from '../apis/authApi';
import { getFilterNamesFromFiltersList, getFiltersList } from '@casops/filters/utils';
import { FilterGroup } from '@casops/filters/types';
import { GeofenceTypeV2 } from '../apis/geofencesApi';

export const FILTERS_SLICE_NAME = 'filters';

// TODO: Increment this number whenever the filter types have been modified.
// This is to make sure that any outdated local storage is not loaded into the slice.
const FILTERS_VERSION = 4;

export interface MultiselectFilters {
  assetGroups: string[];
  configGroups: string[];
  deviceTypes: AssetDeviceType[];
  objectTypes: ObjectType[];
  geofenceTypes: GeofenceTypeV2[];
  geofenceAreas: string[];
  geofenceStyles: string[];
}

export interface SingleSelectFilters {
  softwareStatus: SoftwareStatus;
  healthStatus: HealthStatus;
  geofenceState: GeofenceStateParam;
  duressStatus: boolean | null;
  manDownStatus: boolean | null;
}

// These filters have the exact same type between draft and saved state.
interface FiltersCommonState extends MultiselectFilters {}

// The draft types that the filter inputs are allowed to submit to the store while editing.
export interface FiltersDraftState extends FiltersCommonState {}

export interface FiltersState extends MultiselectFilters, SingleSelectFilters {}

export interface FiltersSliceState {
  draft: FiltersState;
  saved: FiltersState;
  hasFilters: boolean;
}

const initialFiltersState: FiltersState = {
  assetGroups: [],
  configGroups: [],
  deviceTypes: [],
  objectTypes: [],
  geofenceTypes: [],
  geofenceAreas: [],
  geofenceStyles: [],
  geofenceState: GeofenceStateParam.ShowAll,
  softwareStatus: SoftwareStatus.ShowAll,
  healthStatus: HealthStatus.ShowAll,
  duressStatus: false, // active
  manDownStatus: false, // active
};

interface PersistedFiltersState {
  version: number;
  saved: FiltersState;
}

// Type guard func that performs a shallow type check of the local storage data shape.
function isPersistedFilterState(data: unknown): data is PersistedFiltersState {
  return (
    data instanceof Object &&
    'version' in data &&
    typeof data.version === 'number' &&
    'saved' in data
  );
}

const draftToSaved = (draft: FiltersState): FiltersState => {
  const { ...saved } = draft;

  return {
    ...saved,
  };
};

const savedToDraft = (saved: FiltersState): FiltersState => {
  // We always save in a standard format (usually metres-based)
  const { ...draft } = saved;

  return {
    ...draft,
  };
};

// Determine if the draft values are different from the intitial state values.
const getHasFilters = (draft: FiltersState): boolean => {
  return Object.keys(initialFiltersState).some((key: string) => {
    const filterKey: keyof FiltersState = key as keyof FiltersState;
    const draftValue = draft[filterKey];
    const defaultValue = defaultState.draft[filterKey];

    if (Array.isArray(draftValue) && Array.isArray(defaultValue)) {
      // Compare array contents
      return (
        draftValue.length !== defaultValue.length ||
        draftValue.some((v, i) => v !== defaultValue[i])
      );
    }
    return draftValue !== defaultValue;
  });
};

const getLocalStorageKey = () => FILTERS_SLICE_NAME;

const getPersistedData = (): FiltersState | null => {
  const key = getLocalStorageKey();
  const hasKey = Object.keys(window.localStorage).some(k => k === key);

  if (hasKey) {
    try {
      const item = window.localStorage.getItem(key);

      if (item) {
        const data: unknown = JSON.parse(item);

        // Check the data is a valid shape and matches the current version number of the filters.
        if (data && isPersistedFilterState(data) && data.version === FILTERS_VERSION) {
          return data.saved;
        }
      }
    } catch {
      // Swallow
    }
  }

  return null;
};

const writePersistedData = (saved: FiltersState) => {
  try {
    const data: PersistedFiltersState = { version: FILTERS_VERSION, saved };
    window.localStorage.setItem(getLocalStorageKey(), JSON.stringify(data));
  } catch {
    // Swallow
  }
};

const saveAndPersist = (state: Draft<FiltersSliceState>) => {
  // Convert the draft filters to the corresponding data type for the saved state.
  state.saved = draftToSaved(state.draft);

  // Check if at least one filter has been applied
  state.hasFilters = getHasFilters(state.draft);

  // Save to local storage
  writePersistedData(state.saved);
};

export const defaultState: FiltersSliceState = {
  saved: { ...initialFiltersState },
  draft: { ...initialFiltersState },
  hasFilters: false,
};

const filtersSlice = createSlice({
  name: FILTERS_SLICE_NAME,
  initialState: { ...defaultState } as FiltersSliceState,
  reducers: {
    setAssetGroups: (state, action: PayloadAction<string[]>) => {
      state.draft.assetGroups = action.payload;
    },
    setConfigGroups: (state, action: PayloadAction<string[]>) => {
      state.draft.configGroups = action.payload;
    },
    setDeviceTypes: (state, action: PayloadAction<AssetDeviceType[]>) => {
      state.draft.deviceTypes = action.payload;
    },
    setGeofenceAreas: (state, action: PayloadAction<string[]>) => {
      state.draft.geofenceAreas = action.payload;
    },
    setGeofenceState: (state, action: PayloadAction<GeofenceStateParam>) => {
      state.draft.geofenceState = action.payload;
    },
    setGeofenceStyles: (state, action: PayloadAction<string[]>) => {
      state.draft.geofenceStyles = action.payload;
    },
    setGeofenceTypes: (state, action: PayloadAction<GeofenceTypeV2[]>) => {
      state.draft.geofenceTypes = action.payload;
    },
    setHealthStatus: (state, action: PayloadAction<HealthStatus>) => {
      state.draft.healthStatus = action.payload;
    },
    setObjectTypes: (state, action: PayloadAction<ObjectType[]>) => {
      state.draft.objectTypes = action.payload;
    },
    setSoftwareStatus: (state, action: PayloadAction<SoftwareStatus>) => {
      state.draft.softwareStatus = action.payload;
    },
    setDuressStatus: (state, action: PayloadAction<boolean | null>) => {
      state.draft.duressStatus = action.payload;
    },
    setManDownStatus: (state, action: PayloadAction<boolean | null>) => {
      state.draft.manDownStatus = action.payload;
    },
    saveFilters: state => {
      saveAndPersist(state);
    },
    clearDraftFilters: state => ({
      ...state,
      draft: {
        ...initialFiltersState,
      },
    }),
    clearFiltersSubset: (state, action: PayloadAction<{ filterGroup: FilterGroup }>) => {
      const { filterGroup } = action.payload;

      // Get all of the filter keys that belong to the subset.
      const filterNames = getFilterNamesFromFiltersList(getFiltersList(filterGroup));

      // Copy the whole initial filters state to a new object.
      const initialFiltersSubset: Partial<FiltersState> = { ...initialFiltersState };

      // Delete the default filter properties that are not part of the filters subset.
      for (const key in Object.keys(state.draft)) {
        const filterName = key as keyof FiltersState;
        if (!filterNames.includes(filterName)) {
          delete initialFiltersSubset[filterName];
        }
      }

      // Merge the current draft state and a subset of the initial filter values.
      state.draft = {
        ...state.draft,
        ...initialFiltersSubset,
      };

      // Update the saved and hasFilters state.
      saveAndPersist(state);
    },
    // Restore the draft filters to match the saved state
    resetDraftFilters: state => {
      state.draft = savedToDraft(state.saved);
    },
  },
  extraReducers: builder => {
    builder.addMatcher(authApi.endpoints.me.matchFulfilled, (state, { payload }) => {
      const saved: FiltersState | null = getPersistedData();

      if (saved) {
        // Convert the saved values to valid draft values.
        const draft = savedToDraft(saved);

        // Update the 'hasFilters' property based on the new saved/draft state.
        const hasFilters = getHasFilters(draft);

        return {
          saved,
          draft,
          hasFilters,
        };
      }
    });
  },
});

export const { reducer: filtersReducer, actions: filtersActions } = filtersSlice;

export const {
  setAssetGroups,
  setConfigGroups,
  setDeviceTypes,
  setGeofenceAreas,
  setGeofenceState,
  setGeofenceStyles,
  setGeofenceTypes,
  setHealthStatus,
  setObjectTypes,
  setSoftwareStatus,
  setDuressStatus,
  setManDownStatus,
  saveFilters,
  clearDraftFilters,
  clearFiltersSubset,
  resetDraftFilters,
} = filtersActions;

export default filtersSlice;
